import React from 'react';
import { useParams } from 'react-router';
import { Certification as CertificationConst } from '@axiom/const';
import {
  Button,
  CandidateAccolades,
  CandidateDescription,
  CandidateHighlightExperienceDetails,
  CandidateHighlightExperienceSummary,
  CandidateHighlightsUtil,
  CandidatePerformanceFeedback,
  Card,
  CardHeader,
  CardSection,
  Gutter,
  Layout,
  LayoutItem,
  LegalTechSkillsDisplay,
  Paragraph,
  SmallHeader,
  TalentCertifications,
  TalentEducation,
  TalentExperiences,
  TalentLanguages,
  TalentQualifications,
  useApi,
  Well,
} from '@axiom/ui';

import { UserApi } from '../../api/user-api';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateLegalTechSkillsApi } from '../../api/candidate-legal-tech-skills-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { CandidateReputationApi } from '../../api/candidate-reputation-api';
import ProfileCheckmark from '../../public/svg/profile-checkmark.svg';

import { ProfileClientViewCandidateCardHeader } from './ProfileClientViewCandidateCardHeader';

const awardTypes = Object.values(CertificationConst.QUALIFICATION_AWARDS);

export type ProfileClientViewProps = {
  name?: string;
};

export const ProfileClientView = ({
  name = 'PROFILE_CLIENT_VIEW',
}: ProfileClientViewProps = {}) => {
  const { candidateId: urlCandidateId } = useParams();
  const [{ data: user }] = useApi(UserApi.readUser());

  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );

  const [
    { data: candidate },
    { data: taxonomy },
    { data: groupedExperiences },
    { data: legalTechSkills },
    { data: candidateReputation },
  ] = useApi(
    CandidateApi.readCandidate(candidateId),
    TaxonomyApi.readTaxonomy(),
    CandidateExperienceApi.readGroupedExperiences(candidateId),
    CandidateLegalTechSkillsApi.readLegalTechSkills(candidateId),
    CandidateReputationApi.readReputationData(candidateId)
  );

  return (
    <div data-test={name}>
      <Gutter bottom="24px">
        <Button
          name="EXPERIENCE_EDIT_HEADER_BACK_TO_PROFILE"
          variation="minimal"
          icon="arrow-left"
          iconPosition="left"
          to=".."
        >
          Back to profile
        </Button>
      </Gutter>
      <Card>
        <CardHeader>
          <ProfileClientViewCandidateCardHeader
            candidate={candidate}
            name="PROFILE_CLIENT_VIEW_CANDIDATE_HEADER"
          />
        </CardHeader>
        {!!candidate.candidateSummary && (
          <CardSection divider name="profile-client-view-description">
            <CandidateDescription
              description={candidate.candidateSummary}
              calculatedFirstName={candidate.calculatedFirstName}
            />
          </CardSection>
        )}
      </Card>
      <Gutter bottom="16px" />
      {CandidateHighlightsUtil.calculateNumberOfHighlights(candidate) > 0 && (
        <Card name="profile-client-view-highlights">
          <CardHeader>
            <SmallHeader>Highlights</SmallHeader>
          </CardHeader>
          <CardSection>
            <CandidateHighlightExperienceSummary
              taxonomy={taxonomy}
              candidate={candidate}
            />
            <Gutter top="16px">
              <CandidateHighlightExperienceDetails
                candidate={candidate}
                taxonomy={taxonomy}
              />
            </Gutter>
          </CardSection>
        </Card>
      )}
      <Gutter bottom="16px" />
      <Card name="profile-client-view-reputation">
        <CardHeader>
          <SmallHeader>Axiom Performance</SmallHeader>
        </CardHeader>
        <CardSection>
          <Layout verticalGutter="24px" direction="vertical">
            <CandidateAccolades candidateReputation={candidateReputation} />
            {candidate.showReputation === false ? (
              <Well background="light blue" name="optedOutDisplay">
                <Paragraph>
                  {candidate.calculatedDisplayName ?? 'Talent'} has chosen not
                  to display client feedback.
                </Paragraph>
              </Well>
            ) : (
              <div>
                {candidateReputation?.displayableSurveyScoreData ? (
                  <CandidatePerformanceFeedback
                    candidateReputation={candidateReputation}
                  />
                ) : (
                  <Well background="light blue" name="limitedFeedbackDisplay">
                    <Layout horizontalGutter="16px" position="middle">
                      <LayoutItem>
                        <ProfileCheckmark width="98" height="93" />
                      </LayoutItem>
                      <Paragraph>
                        Although client feedback is not yet available for this
                        talent, we are confident they are a great match for your
                        legal needs.
                      </Paragraph>
                    </Layout>
                  </Well>
                )}
              </div>
            )}
          </Layout>
        </CardSection>
      </Card>
      <Gutter bottom="16px" />
      {groupedExperiences.length > 0 && (
        <Gutter bottom="16px">
          <Card name="profile-client-view-experience">
            <CardHeader>
              <SmallHeader>Experience</SmallHeader>
            </CardHeader>
            <TalentExperiences groupedExperiences={groupedExperiences} />
          </Card>
        </Gutter>
      )}
      {legalTechSkills.length > 0 && (
        <Gutter bottom="16px">
          <Card name="profile-client-view-legal-tech-skills">
            <CardHeader>
              <SmallHeader>Legal Tech Skills</SmallHeader>
            </CardHeader>
            <CardSection>
              <LegalTechSkillsDisplay
                legalTechSkills={legalTechSkills}
                taxonomy={taxonomy}
              />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidate.degrees?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="profile-client-view-education">
            <CardHeader>
              <SmallHeader>Education</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentEducation candidate={candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {candidate.certifications && candidate.certifications.length > 0 ? (
        <Gutter bottom="16px">
          <Card name="profile-client-view-accomplishments">
            <CardHeader>
              <SmallHeader>Accomplishments</SmallHeader>
            </CardHeader>
            {candidate.certifications.some(
              ({ award }) => !awardTypes.includes(award)
            ) && (
              <CardSection divider name="certifications-subsection">
                <TalentCertifications candidate={candidate} />
              </CardSection>
            )}
            {candidate.certifications.some(({ award }) =>
              awardTypes.includes(award)
            ) && (
              <CardSection name="qualifications-subsection">
                <TalentQualifications candidate={candidate} />
              </CardSection>
            )}
          </Card>
        </Gutter>
      ) : null}
      {(candidate.languages?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="profile-client-view-languages">
            <CardHeader>
              <SmallHeader>Languages</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentLanguages candidate={candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
    </div>
  );
};
